import React from "react";
import { Tag } from "antd";
import { entityFieldMappings } from "./entityFieldMappings";

/**
 * Gets the display label for a field based on its entity and field name
 * @param {string} entity - The entity type (e.g., 'patient', 'employee', 'company')
 * @param {string} fieldName - The name of the field
 * @param {string} value_type - The type of the value (e.g., 'const')
 * @param {string} value - The value to be displayed if value_type is 'const'
 * @returns {string} The human-readable label for the field
 */
const getFieldLabel = (entity, fieldName, value_type, value) => {
  if (value_type === "const") {
    return value; // For const values, just return the value itself
  }

  const field = entityFieldMappings[entity]?.find((f) => f.value === fieldName);
  const baseLabel = field?.label || fieldName;

  // If this is a list field (subfield), show the subfield name in parentheses
  if (value && typeof value === "string" && fieldName !== value) {
    return `${baseLabel} (${value})`;
  }

  return baseLabel;
};

/**
 * A draggable marker component for positioning fields on a PDF form
 * @component
 * @param {Object} props - Component props
 * @param {Object} props.position - The position of the marker on the page
 * @param {number} props.position.x - X coordinate
 * @param {number} props.position.y - Y coordinate
 * @param {string} props.field - The field identifier
 * @param {string} props.entity - The entity type (patient, employee, company)
 * @param {string} [props.value] - Optional value associated with the field marker
 * @param {string} [props.value_type] - The type of the value (e.g., 'const')
 * @param {string} [props.color='blue'] - Color of the marker ('blue' or 'red')
 * @param {Function} props.onDragStart - Handler for drag start event
 * @param {Function} props.onDrag - Handler for drag event
 * @param {Function} props.onDragEnd - Handler for drag end event
 * @param {Function} [props.onDelete] - Optional handler for marker deletion
 * @returns {React.ReactElement} A field marker component with drag functionality
 */
const FieldMarker = ({
  position,
  field,
  entity,
  value,
  value_type,
  color = "blue",
  onDragStart,
  onDrag,
  onDragEnd,
  onDelete,
}) => {
  const displayLabel = getFieldLabel(entity, field, value_type, value);
  const isLongLabel = displayLabel?.length > 30;

  return (
    <div>
      <div
        style={{
          position: "absolute",
          left: position.x,
          top: position.y,
          zIndex: 2,
        }}
      >
        <div
          style={{
            width: 12,
            height: 12,
            backgroundColor: `rgba(${
              color === "blue" ? "0, 100, 255" : "255, 0, 0"
            }, 0.5)`,
            border: `2px solid ${color}`,
            cursor: "move",
            transform: "translate(-100%, -100%)",
          }}
          draggable="true"
          onDragStart={onDragStart}
          onDrag={onDrag}
          onDragEnd={onDragEnd}
        />
      </div>
      <div
        style={{
          position: "absolute",
          left: position.x + 3,
          top: position.y - 18,
          zIndex: 2,
        }}
      >
        <Tag
          color={color === "blue" ? "magenta" : "red"}
          closable
          style={{ userSelect: "none" }}
          onClose={(e) => {
            e.stopPropagation();
            onDelete?.();
          }}
        >
          {isLongLabel ? `${displayLabel.slice(0, 30)}...` : displayLabel}
        </Tag>
      </div>
    </div>
  );
};

export default FieldMarker;
